import React, { useState, useEffect, useCallback, useRef } from "react";
import "./video.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import ClipLoader from "react-spinners/ClipLoader";
import { useParams } from "react-router-dom";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { ThreeDot } from "react-loading-indicators";
import PostActions from "../../components/LikeCommentActions";
import DropdownActions from "../../components/DropdownActions";

const Video = ({ isYourOwnProfile, profileId }) => {
  const { userId: id } = useParams();
  const queryClient = useQueryClient();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [description, setDescription] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [deleteVideoId, setDeleteVideoId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editVideoId, setEditVideoId] = useState(null);
  const [editVideoFile, setEditVideoFile] = useState(null);
  const [editDescription, setEditDescription] = useState("");
  const [editPreviewUrl, setEditPreviewUrl] = useState(null);
  const initialUserId = localStorage.getItem("user_id");
  const [currentUserId, setCurrentUserId] = useState(initialUserId);
  const [isUploading, setIsUploading] = useState(false);
  const videoRefs = useRef([]);

  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("access_token");

  const fetchCurrentUser = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/user/profile/create-profile/`, {
        headers: { Authorization: `Bearer ${token}`,  "x-api-key": process.env.REACT_APP_X_API_KEY, },
      });
      const data = await res.json();
      if (res.ok) {
        setCurrentUserId(data.data?.user);
        localStorage.setItem("user_id", data.data?.user);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error retrieving user info.");
    }
  }, [BASE_URL, token]);

  // const fetchGalleryVideo = async ({ pageParam = 1 }) => {
  //   let url = null;
  //   if (id) {
  //     url = `${BASE_URL}/user/profile/all-profiles?id=${id}`;
  //   } else {
  //     url = `${BASE_URL}/user/videos/video/?page=${pageParam}`;
  //   }

  //   const res = await fetch(url, {
  //     headers: {
  //       Authorization: `Bearer ${token}`,
  //     },
  //   });

  //   const data = await res.json();

  //   if (!res.ok) {
  //     throw new Error("Failed to fetch video");
  //   }

  //   const sorted = (data.results || []).sort(
  //     (a, b) => new Date(b.created_at) - new Date(a.created_at)
  //   );
  //   return {
  //     video: sorted,
  //     count: data.count,
  //     currentPage: pageParam,
  //     next: data.next ? true : false,
  //   };
  // };

  const fetchGalleryVideo = async ({ pageParam = 1 }) => {
    if (id) {
      // Fetch profile including videos
      const url = `${BASE_URL}/user/profile/all-profiles/?id=${id}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}`, "x-api-key": process.env.REACT_APP_X_API_KEY },
      });
      const json = await res.json();

      if (!res.ok) throw new Error("Failed to fetch videos");

      const videos = json.data?.videos || [];

      const sortedVideos = videos
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .map((video) => ({
          ...video,
          isLiked: video.is_liked,
          likes_count: video.total_likes || 0,
        }));
      return {
        video: sortedVideos,
        count: videos.length,
        currentPage: 1,
        next: false,
      };
    } else {
      const url = `${BASE_URL}/user/videos/video/?page=${pageParam}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
      });

      const data = await res.json();

      if (!res.ok) throw new Error("Failed to fetch videos");

      const sorted = (data.results || []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      return {
        video: sorted,
        count: data.count,
        currentPage: pageParam,
        next: data.next ? true : false,
      };
    }
  };

  useEffect(() => {
    if (isYourOwnProfile) {
      fetchCurrentUser();
    }
  }, [fetchCurrentUser, isYourOwnProfile]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("video/")) {
      setSelectedVideo(file);
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      toast.error("Please select a valid video file");
      event.target.value = null;
    }
  };

  const handleUploadClick = () => {
    document.getElementById("video-upload-input").click();
  };

  const handleUpload = async () => {
    if (!selectedVideo) {
      toast.warning("Please select a video and enter description.");
      return;
    }

    const formData = new FormData();
    formData.append("video", selectedVideo);
    formData.append("description", description);
    try {
      setIsUploading(true);
      const res = await fetch(`${BASE_URL}/user/videos/video/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Video uploaded successfully!");
        setSelectedVideo(null);
        setDescription("");
        setPreviewUrl(null);
        queryClient.setQueryData(["video"], (oldData) => {
          if (!oldData) {
            return {
              pageParams: [undefined],
              pages: [
                {
                  video: [data?.data],
                  count: 1,
                  currentPage: 1,
                  next: false,
                },
              ],
            };
          }

          return {
            pageParams: oldData.pageParams,
            pages: oldData.pages.map((page, idx) =>
              idx === 0
                ? {
                    ...page,
                    video: [data?.data, ...page.video],
                  }
                : page
            ),
          };
        });
      } else {
        toast.error(data.message || "Upload failed.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Upload failed.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleEditClick = (video) => {
    setEditMode(true);
    setEditVideoId(video.id);
    setEditDescription(video.description || "");
    setEditPreviewUrl(video.video);
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("video/")) {
      setEditVideoFile(file);
      setEditPreviewUrl(URL.createObjectURL(file));
    } else {
      toast.error("Select a valid video file.");
    }
  };

  const handleEditSubmit = async () => {
    if (!editVideoId) {
      toast.warning("Please enter description.");
      return;
    }

    const formData = new FormData();
    formData.append("id", editVideoId);
    formData.append("description", editDescription);
    if (editVideoFile) {
      formData.append("video", editVideoFile);
    }
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/user/videos/video/`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
        body: formData,
      });

      const data = await res.json();

      if (res.ok) {
        toast.success("Video updated successfully!");
        setEditMode(false);
        setEditVideoId(null);
        setEditVideoFile(null);
        setEditDescription("");
        setEditPreviewUrl(null);
        queryClient.setQueryData(["video"], (oldData) => {
          if (!oldData) return oldData;

          return {
            pageParams: oldData.pageParams,
            pages: oldData.pages.map((page) => ({
              ...page,
              video: page.video.map((video) =>
                video.id === editVideoId ? data.data : video
              ),
            })),
          };
        });
      } else {
        toast.error(data.message || "Update failed.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating video.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (videoId) => {
    setDeleteVideoId(videoId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const res = await fetch(`${BASE_URL}/user/videos/video/`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-api-key": process.env.REACT_APP_X_API_KEY
        },
        body: JSON.stringify({ id: deleteVideoId }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Video deleted successfully!");
        setShowDeleteModal(false);
        setDeleteVideoId(null);
        queryClient.setQueryData(["video"], (oldData) => {
          if (!oldData) return oldData;

          return {
            pageParams: oldData.pageParams,
            pages: oldData.pages.map((page) => ({
              ...page,
              video: page.video.filter((video) => video.id !== deleteVideoId),
            })),
          };
        });
      } else {
        toast.error(data?.message || "Deletion failed.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Deletion error.");
    }
  };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["video"],
      queryFn: fetchGalleryVideo,
      getNextPageParam: (lastPage, pages) => {
        return lastPage.next ? pages.length + 1 : undefined;
      },
      initialPageParam: 1,
    });

  const videoData = data?.pages.flatMap((page) => page.video) || [];

  const observerRef = useRef();
  const lastImageRef = useCallback(
    (node) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && hasNextPage) {
            fetchNextPage();
          }
        },
        {
          root: null,
          rootMargin: "100px",
          threshold: 0,
        }
      );

      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const vid = entry.target;
          if (entry.intersectionRatio < 0.5 && !vid.paused) {
            vid.pause();
          }
        });
      },
      { threshold: Array.from({ length: 101 }, (_, i) => i / 100) } 
    );

    videoRefs.current.forEach((vid) => {
      if (vid) observer.observe(vid);
    });

    return () => {
      videoRefs.current.forEach((vid) => {
        if (vid) observer.unobserve(vid);
      });
    };
  }, [videoData]);

  return (
    //   <div className="video-page">
    //   <ToastContainer />
    //   <div className="video-container">
    //     <div className="heading-btn-container">
    //       <h2>Upload Videos and Presentations</h2>
    //       {(id === currentUserId || !id) && (
    //         <>
    //           <button className="video-upload-btn" onClick={handleUploadClick}>
    //             <Plus size={18}></Plus>
    //           </button>
    //         </>
    //       )}

    //       <input
    //         type="file"
    //         id="video-upload-input"
    //         accept="video/*"
    //         style={{ display: "none" }}
    //         onChange={handleFileChange}
    //       />
    //     </div>

    //     {selectedVideo && (
    //       <div className="upload-preview-box">
    //         <video controls src={previewUrl} className="video-preview" />
    //         <textarea
    //           placeholder="Enter video description..."
    //           value={description}
    //           onChange={(e) => setDescription(e.target.value)}
    //           className="description-input"
    //         />
    //         <button
    //           className="submit-upload-btn"
    //           onClick={handleUpload}
    //           disabled={isUploading}
    //         >
    //           {isUploading ? (
    //             <ThreeDot color={["#4A90E2"]} size="small" />
    //           ) : (
    //             "submit"
    //           )}{" "}
    //         </button>
    //       </div>
    //     )}
    //     <div>
    //       {isLoading ? (
    //         <div className="loading-snippets">
    //           <ClipLoader color="#4A90E2" size={40} />
    //         </div>
    //       ) : videoData.length === 0 ? (
    //         <p>No Videos available.</p>
    //       ) : (
    //         <div className="video-grid">
    //           {(videoData || []).map((video, index) => {
    //             if (!video || !video.video) return null;
    //             return (
    //               <div className="video-card" key={`video-${index}`}>
    //                 {Number(video.user) === Number(currentUserId) && (
    //                   <div className="dropdown-wrapper">
    //                     <DropdownActions
    //                       onEdit={() => handleEditClick(video)}
    //                       onDelete={() => handleDeleteClick(video.id)}
    //                     />
    //                   </div>
    //                 )}
    //                 {editMode && editVideoId === video.id && (
    //                   <div className="upload-preview-box">
    //                     <video
    //                       ref={(el) => (videoRefs.current[index] = el)}
    //                       controls
    //                       src={editPreviewUrl}
    //                       className="video-preview"
    //                     />
    //                     <textarea
    //                       placeholder="Edit video description..."
    //                       value={editDescription}
    //                       onChange={(e) => setEditDescription(e.target.value)}
    //                       className="description-input"
    //                     />
    //                     <input
    //                       type="file"
    //                       accept="video/*"
    //                       onChange={handleEditFileChange}
    //                       style={{ marginBottom: "10px" }}
    //                     />
    //                     <div
    //                       style={{ display: "flex", gap: "10px" }}
    //                       className="buttons_outer_submit"
    //                     >
    //                       <button
    //                         className="submit-upload-btn"
    //                         onClick={handleEditSubmit}
    //                       >
    //                         {loading ? (
    //                           <ThreeDot color={["#4A90E2"]} size="small" />
    //                         ) : (
    //                           "Save Changes"
    //                         )}{" "}
    //                       </button>
    //                       <button
    //                         className="cancel-btn"
    //                         onClick={() => {
    //                           setEditMode(false);
    //                           setEditVideoId(null);
    //                           setEditDescription("");
    //                           setEditVideoFile(null);
    //                           setEditPreviewUrl(null);
    //                         }}
    //                       >
    //                         Cancel
    //                       </button>
    //                     </div>
    //                   </div>
    //                 )}
    //                 <div className="description-text">
    //                   {(video.description || "").split("\n").map((line, i) => (
    //                     <p key={i}>
    //                       {line.split(/(#[a-zA-Z0-9_]+)/g).map((part, idx) =>
    //                         part.startsWith("#") ? (
    //                           <span key={idx} className="hashtag">
    //                             {part}
    //                           </span>
    //                         ) : (
    //                           <React.Fragment key={idx}>{part}</React.Fragment>
    //                         )
    //                       )}
    //                     </p>
    //                   ))}
    //                 </div>
    //                 <video
    //                   src={video.video}
    //                   controls
    //                   ref={(el) => (videoRefs.current[index] = el)}
    //                 />
    //                 <div>
    //                   {/* <div className="video_bottom"> */}
    //                   <PostActions
    //                     postId={video.id}
    //                     type="video"
    //                     is_Liked={video.is_liked === true}
    //                     likeCount={video.total_likes}
    //                     currentUser={currentUserId}
    //                   />
    //                 </div>
    //               </div>
    //             );
    //           })}
    //         </div>
    //       )}
    //       <div ref={lastImageRef} style={{ height: 1 }} />
    //       {isFetchingNextPage && (
    //         <div className="loading-snippets">
    //           <ClipLoader color="#4A90E2" size={40} />
    //         </div>
    //       )}
    //     </div>
    //   </div>

    //   {showDeleteModal && (
    //     <div className="delete-modal-overlay">
    //       <div className="delete-modal">
    //         <h3>Are you sure you want to delete this video?</h3>
    //         <p>This action cannot be undone.</p>
    //         <div className="modal-actions">
    //           <button className="confirm-btn" onClick={confirmDelete}>
    //             Confirm
    //           </button>
    //           <button
    //             className="cancel-btn"
    //             onClick={() => setShowDeleteModal(false)}
    //           >
    //             Cancel
    //           </button>
    //         </div>
    //       </div>
    //     </div>
    //   )}
    // </div>
    <div className="w-full flex items-center justify-center">
      <ToastContainer />
      <div className="w-full max-w-[1050px]">
        <div className="w-full flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-900">Upload Videos and Presentations</h2>
          {(id === currentUserId || !id) && (
            <>
              <button className="ml-4 shrink-0 bg-[#004182] text-white p-3 rounded-full font-medium cursor-pointer border border-[#00449d] flex items-center justify-center" onClick={handleUploadClick}>
                <Plus size={18}></Plus>
              </button>
            </>
          )}

          <input
            type="file"
            id="video-upload-input"
            accept="video/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

       {selectedVideo && (
  <div className="mt-5 mb-8 flex flex-col items-center border-2 border-dashed border-gray-300 rounded-xl bg-slate-50 p-3 md:p-8">
    {/* Video Preview */}
    <div className="mb-6 w-full ">
      <video
        controls
        src={previewUrl}
        className="w-full h-[400px] aspect-video rounded-[10px] object-cover shadow-md"
      />
    </div>

    {/* Description Input */}
    <div className="mb-6 w-full ">
      <textarea
        placeholder="Enter video description..."
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm outline-none min-h-[80px] max-h-[120px] resize-y focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
      />
    </div>
  
    {/* Submit Button */}
    <div className="w-full  flex justify-center">
      <button
        onClick={handleUpload}
        disabled={isUploading}
        className={`w-full max-w-[140px] py-2.5 text-sm font-medium rounded-xl transition-all duration-200 flex items-center justify-center gap-2 ${
          isUploading
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-[#0f6abf] text-white hover:bg-[#0a5ca8]"
        }`}
      >
        {isUploading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Uploading...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            Upload Video
          </>
        )}
      </button>
    </div>
  </div>
)}
        <div>
          {isLoading ? (
            <div className="loading-snippets">
              <ClipLoader color="#4A90E2" size={40} />
            </div>
          ) :videoData.length === 0 ? (
  <div className="mt-5 flex flex-col justify-center items-center py-12 bg-white rounded-xl border border-gray-200">
    <div className="w-16 h-16 bg-[#e8f0f7] rounded-xl flex items-center justify-center mb-4">
      <svg 
        className="w-8 h-8 text-[#0f6abf]" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth="2" 
          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" 
        />
      </svg>
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">No videos yet</h3>
    <p className="text-gray-500 text-sm mb-4">Upload your first video to get started</p>
  </div>
) : (
         <div className="space-y-6 pt-6">
  {(videoData || []).map((video, index) => {
    if (!video || !video.video) return null;
    return (
      <div
        key={`video-${index}`}
        className="relative flex flex-col bg-white p-2 md:p-6 rounded-xl border border-gray-200 shadow-sm transition-all hover:shadow-md"
      >
        {/* Dropdown Actions */}
        {Number(video.user) === Number(currentUserId) && (
          <div className="absolute right-0 top-[-25px] z-10">
            <DropdownActions
              onEdit={() => handleEditClick(video)}
              onDelete={() => handleDeleteClick(video.id)}
            />
          </div>
        )}

        {/* Edit Mode */}
        {editMode && editVideoId === video.id && (
          <div className="mt-8 md:mt-5 flex flex-col items-center bg-slate-50 p-2 md:p-6 border-2 border-dashed border-gray-200 rounded-2xl mb-6 w-full">
            <video
              ref={(el) => (videoRefs.current[index] = el)}
              controls
              src={editPreviewUrl}
              className="w-full h-[400px] aspect-video object-cover rounded-xl mb-4 shadow-sm"
            />
            <textarea
              placeholder="Edit video description..."
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              className="w-full p-4 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 mb-4 resize-y min-h-[80px]"
            />
            <input
              type="file"
              accept="video/*"
              onChange={handleEditFileChange}
              className="w-full p-3 rounded-xl bg-white border border-gray-300 text-gray-900 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 mb-4"
            />
            <div className="flex gap-3 w-full">
              <button
                className="flex-1 py-3 bg-[#0f6abf] text-white text-sm font-medium rounded-xl hover:bg-[#0a5ca8] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                onClick={handleEditSubmit}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
              <button
                className="flex-1 py-3 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setEditMode(false);
                  setEditVideoId(null);
                  setEditDescription("");
                  setEditVideoFile(null);
                  setEditPreviewUrl(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Description */}
        <div className="text-gray-900 font-medium text-sm mb-4 max-w-full break-words">
          {(video.description || "").split("\n").map((line, i) => (
            <p key={i} className="text-gray-700 leading-relaxed mb-2">
              {line.split(/(#[a-zA-Z0-9_]+)/g).map((part, idx) =>
                part.startsWith("#") ? (
                  <span key={idx} className="text-[#0f6abf] font-medium">
                    {part}
                  </span>
                ) : (
                  <React.Fragment key={idx}>{part}</React.Fragment>
                )
              )}
            </p>
          ))}
        </div>

        {/* Video Player */}
        <video
          src={video.video}
          controls
          ref={(el) => (videoRefs.current[index] = el)}
          className="w-full h-[400px] aspect-video object-cover rounded-xl mb-4 shadow-sm"
        />

        {/* Actions */}
        <div>
          <PostActions
            postId={video.id}
            type="video"
            is_Liked={video.is_liked === true}
            likeCount={video.total_likes}
            currentUser={currentUserId}
          />
        </div>
      </div>
    );
  })}
</div>
          )}
          <div ref={lastImageRef} style={{ height: 1 }} />
          {isFetchingNextPage && (
            <div className="loading-snippets">
              <ClipLoader color="#4A90E2" size={40} />
            </div>
          )}
        </div>
      </div>

     {showDeleteModal && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4">
    <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
      {/* Modal Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Delete Video</h3>
            <p className="text-sm text-gray-500 mt-0.5">This action cannot be undone</p>
          </div>
        </div>
      </div>

      {/* Modal Content */}
      <div className="p-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Are you sure you want to delete this video?</h4>
          <p className="text-gray-600">This video will be permanently removed from your gallery.</p>
        </div>
      </div>

      {/* Modal Footer */}
      <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100">
        <div className="flex gap-3">
          <button
            className="flex-1 px-5 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            className="flex-1 px-5 py-2.5 bg-red-500 text-white font-medium rounded-xl hover:bg-red-600 transition-colors"
            onClick={confirmDelete}
          >
            Delete Video
          </button>
        </div>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default Video;
